import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminPage, Pill } from '@/admin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ProposalFields, seedEntries } from '@/components/ProposalFields';
import { consoleApi, type Proposal, type ProposalStatus } from '@/lib/consoleApi';
import { ConsoleApiError } from '@/lib/consoleApi';
import { validateRangeEntries, type RangeEntry } from '@/lib/proposalValidation';
import { useAuth } from '@/providers/AuthProvider';
import { roleSatisfies } from '@/lib/roleRank';

const TARGET = 'reference_range';

const STATUS_HEX: Record<ProposalStatus, string> = {
  requested: '#F2A93B', integrated: '#2BBAA8', declined: '#8A8F98',
};

function StatusPill({ status }: { status: ProposalStatus }) {
  return <Pill label={status} color={STATUS_HEX[status]} />;
}

/** Admin-only inline integrate / decline actions on a requested proposal. */
function ResolveActions({ proposal, onDone }: { proposal: Proposal; onDone: () => void }) {
  const [mode, setMode] = useState<'idle' | 'integrate' | 'decline'>('idle');
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const integrate = useMutation({
    mutationFn: () => consoleApi.proposals.integrate(proposal.id, { note: text || null }),
    onSuccess: () => { setMode('idle'); setText(''); onDone(); },
    onError: (e) => setErr(e instanceof ConsoleApiError ? e.detail : String(e)),
  });
  const decline = useMutation({
    mutationFn: () => consoleApi.proposals.decline(proposal.id, { reason: text }),
    onSuccess: () => { setMode('idle'); setText(''); onDone(); },
    onError: (e) => setErr(e instanceof ConsoleApiError ? e.detail : String(e)),
  });
  const busy = integrate.isPending || decline.isPending;

  if (mode === 'idle') {
    return (
      <div className="flex gap-8">
        <Button size="sm" variant="default" onClick={() => { setErr(null); setMode('integrate'); }}>Integrate</Button>
        <Button size="sm" variant="outline" onClick={() => { setErr(null); setMode('decline'); }}>Decline</Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-8">
      <Input
        placeholder={mode === 'integrate' ? 'Integration note (e.g. seeded + verified read)' : 'Reason for declining (required)'}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {err ? <p className="text-body-sm text-flagged">{err}</p> : null}
      <div className="flex gap-8">
        <Button
          size="sm"
          variant={mode === 'decline' ? 'destructive' : 'default'}
          disabled={busy || (mode === 'decline' && !text.trim())}
          onClick={() => (mode === 'integrate' ? integrate.mutate() : decline.mutate())}
        >
          {busy ? '…' : mode === 'integrate' ? 'Confirm integration' : 'Confirm decline'}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setMode('idle'); setErr(null); }}>Cancel</Button>
      </div>
    </div>
  );
}

export function ProposalPage() {
  const qc = useQueryClient();
  const { role } = useAuth();
  const isAdmin = roleSatisfies(role ?? '', 'admin');

  const [slugInput, setSlugInput] = useState('anti-tpo');
  const [loaded, setLoaded] = useState<string | null>(null);
  const [entries, setEntries] = useState<RangeEntry[]>([]);
  const [note, setNote] = useState('');
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [submittedId, setSubmittedId] = useState<string | null>(null);

  const target = useQuery({
    queryKey: ['proposal-target', TARGET, loaded],
    queryFn: () => consoleApi.proposals.target(TARGET, loaded as string),
    enabled: !!loaded,
  });
  const proposals = useQuery({
    queryKey: ['proposals', TARGET],
    queryFn: () => consoleApi.proposals.list({ target: TARGET }),
  });

  // Seed the editable entries from the locked current value when the target loads.
  useEffect(() => {
    if (target.data) {
      setEntries(seedEntries(target.data.field_schema, target.data.current));
      setSubmittedId(null);
      setSubmitErr(null);
    }
  }, [target.data]);

  const validationError = entries.length ? validateRangeEntries(entries) : 'Load a biomarker to propose a range.';

  const submit = useMutation({
    mutationFn: () => consoleApi.proposals.submit({
      target: TARGET,
      item_id: target.data!.item_id,
      item_label: target.data!.item_label,
      scope: target.data!.scope,
      proposed_value: entries,
      note: note || null,
    }),
    onSuccess: (res) => {
      setSubmittedId(res.item.id);
      setNote('');
      void qc.invalidateQueries({ queryKey: ['proposals', TARGET] });
    },
    onError: (e) => setSubmitErr(e instanceof ConsoleApiError ? e.detail : String(e)),
  });

  const refreshList = () => void qc.invalidateQueries({ queryKey: ['proposals', TARGET] });

  return (
    <AdminPage
      title="Reference ranges — propose & integrate"
      description="Propose a clinical reference range whose value we cannot author ourselves. You see the locked current value and propose a new one; requesting integration hands it to us to write into the seed and verify. Nothing is written to the biomarker DB until we integrate."
    >
      {/* Biomarker loader */}
      <div className="mb-21 flex items-end gap-8">
        <label className="flex flex-col gap-4">
          <span className="text-[11px] text-text-muted">Biomarker (canonical slug)</span>
          <Input
            className="w-64"
            value={slugInput}
            onChange={(e) => setSlugInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setLoaded(slugInput.trim().toLowerCase()); }}
            placeholder="e.g. anti-tpo"
          />
        </label>
        <Button data-testid="load-biomarker" variant="secondary" onClick={() => setLoaded(slugInput.trim().toLowerCase())}>Load</Button>
      </div>

      {target.isError ? <p className="text-body-sm text-flagged">Could not load that biomarker.</p> : null}

      {target.data ? (
        <div className="mb-34 flex flex-col gap-13">
          {!target.data.biomarker_known ? (
            <p className="rounded-md border border-flagged/40 bg-flagged/10 p-13 text-body-sm text-flagged">
              <strong>{target.data.item_id}</strong> is not a known canonical biomarker — integration will fail the
              foreign-key check. Confirm the slug before proposing.
            </p>
          ) : null}

          {/* Locked current value */}
          <div>
            <h2 className="text-h3 font-semibold">Current (locked)</h2>
            {target.data.current.length ? (
              <table className="mt-8 w-full border-collapse text-body-sm">
                <thead>
                  <tr className="text-left text-text-muted">
                    <th className="py-4 pr-13">Sex</th><th className="py-4 pr-13">Unit</th>
                    <th className="py-4 pr-13">Low</th><th className="py-4 pr-13">High</th>
                    <th className="py-4 pr-13">Optimal</th>
                  </tr>
                </thead>
                <tbody>
                  {target.data.current.map((c, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="py-4 pr-13">{String(c.sex)}</td>
                      <td className="py-4 pr-13">{String(c.unit ?? '—')}</td>
                      <td className="py-4 pr-13">{c.range_low ?? '—'}</td>
                      <td className="py-4 pr-13">{c.range_high ?? '—'}</td>
                      <td className="py-4 pr-13">{c.range_optimal_low ?? '—'}–{c.range_optimal_high ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p data-testid="no-current-range" className="mt-4 text-body-sm text-text-secondary">No range on file — this biomarker has no reference range yet.</p>
            )}
          </div>

          {/* Editable proposed value */}
          <div>
            <h2 className="mb-8 text-h3 font-semibold">Proposed</h2>
            <ProposalFields schema={target.data.field_schema} value={entries} onChange={setEntries} />
          </div>

          {/* Note + submit */}
          <label className="flex flex-col gap-4">
            <span className="text-[11px] text-text-muted">Note (optional — source / rationale)</span>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. AU RCPA adult reference interval" />
          </label>

          {validationError ? <p className="text-body-sm text-text-secondary">{validationError}</p> : null}
          {submitErr ? <p className="text-body-sm text-flagged">{submitErr}</p> : null}
          {submittedId ? <p className="text-body-sm text-success">Integration requested — it now appears below as “requested”.</p> : null}

          <div>
            <Button
              data-testid="request-integration"
              disabled={!!validationError || submit.isPending || !target.data.biomarker_known}
              onClick={() => { setSubmitErr(null); submit.mutate(); }}
            >
              {submit.isPending ? 'Requesting…' : 'Request integration'}
            </Button>
          </div>
        </div>
      ) : null}

      {/* Pending & recent proposals */}
      <div>
        <h2 className="mb-8 text-h3 font-semibold">Proposals</h2>
        {proposals.isLoading ? <p className="text-body-sm text-text-muted">Loading…</p> : null}
        {proposals.data && proposals.data.length === 0 ? (
          <p className="text-body-sm text-text-secondary">No proposals yet.</p>
        ) : null}
        <div data-testid="proposals-list" className="flex flex-col gap-8">
          {(proposals.data ?? []).map((p) => (
            <div key={p.id} className="flex items-start justify-between gap-13 rounded-md border border-border p-13">
              <div className="min-w-0">
                <div className="flex items-center gap-8">
                  <span className="font-mono text-body-sm">{p.item_id}</span>
                  <StatusPill status={p.status} />
                </div>
                <p className="mt-4 truncate text-[11px] text-text-muted">
                  by {p.reviewer} · {new Date(p.timestamp).toLocaleString()}
                  {p.note ? ` · ${p.note}` : ''}
                </p>
                <pre className="mt-4 overflow-x-auto rounded bg-surface-sunken p-8 text-[11px]">{JSON.stringify(p.proposed_value, null, 1)}</pre>
                {p.resolution ? (
                  <p className="mt-4 text-[11px] text-text-secondary">
                    {p.resolution.outcome} by {p.resolution.integrator}
                    {p.resolution.note ? ` — ${p.resolution.note}` : ''}
                  </p>
                ) : null}
              </div>
              {isAdmin && p.status === 'requested' ? (
                <ResolveActions proposal={p} onDone={refreshList} />
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </AdminPage>
  );
}
